import { useCallback, useEffect, useMemo, useRef } from 'react';

import api from 'api';

// Embed tokens are short lived (~60 min by default). Swap a fresh one in this
// far ahead of the stated expiry so a slow round trip never leaves the report
// holding a dead token.
const REFRESH_MARGIN_MS = 10 * 60 * 1000;
// How often we compare the clock against the expiry. Cheap (no network) and,
// unlike a single long timer, immune to the drift a throttled tab or a sleeping
// machine introduces — those suspend timers, so a timer set an hour out can
// come back long after the token is already dead.
const CHECK_INTERVAL_MS = 60 * 1000;
// Used when the response carries no usable expiry: refresh this long after the
// token was issued instead. Comfortably inside the default lifetime.
const FALLBACK_LIFETIME_MS = 45 * 60 * 1000;
const RETRY_DELAY_MS = 30 * 1000;
const MAX_BACKOFF_STEPS = 4;

// A .NET DateTime with an unspecified Kind serializes with no timezone
// designator, which Date.parse reads as browser-local time. The API deals in
// UTC, so pin it there rather than silently inheriting the viewer's offset —
// east of UTC that would push the refresh past the actual expiry.
const parseExpiry = (value) => {
  if (typeof value !== 'string' || !value) return null;
  const normalized = /(Z|[+-]\d{2}:?\d{2})$/.test(value) ? value : `${value}Z`;
  const parsed = Date.parse(normalized);
  return Number.isNaN(parsed) ? null : parsed;
};

const getTimeToExpiry = (tokenExpiry) => {
  const expiresAt = parseExpiry(tokenExpiry);
  return expiresAt === null ? null : expiresAt - Date.now();
};

/**
 * Keeps an embedded Power BI report authenticated for as long as it stays on
 * screen: fetches a fresh embed token before the current one expires and hands
 * it to the report in place.
 *
 * Returns the props to spread onto `PowerBIEmbed`.
 */
const useEmbedTokenRefresh = (serviceLine, tokenExpiry) => {
  const reportRef = useRef(null);
  const expiryRef = useRef(tokenExpiry);
  const refreshRef = useRef(null);

  const setReport = useCallback((report) => {
    reportRef.current = report;
  }, []);

  useEffect(() => {
    expiryRef.current = tokenExpiry;

    let cancelled = false;
    let inFlight = false;
    let failures = 0;
    // Epoch ms before which we must not retry, so a failing endpoint is not
    // hammered once per check.
    let nextAttemptAt = 0;
    let tokenObtainedAt = Date.now();

    async function refresh(recover = false) {
      if (cancelled || inFlight || Date.now() < nextAttemptAt) return;
      inFlight = true;
      try {
        const { data } = await api.getAnalyticsDashboardUrl(serviceLine);
        if (cancelled) return;

        const report = reportRef.current;
        // Treat a missing instance as a failure rather than a silent no-op:
        // otherwise we would mark the refresh a success and let the report keep
        // running on the dead token.
        if (!report) throw new Error('Power BI report is not embedded yet.');

        // Swapping the token in place keeps the report's current page, filters
        // and scroll position. Re-embedding with a new config would not — the
        // user would see the report blink back to its initial state.
        await report.setAccessToken(data.embedToken);
        if (cancelled) return;

        expiryRef.current = data.tokenExpiry;
        tokenObtainedAt = Date.now();
        failures = 0;
        nextAttemptAt = 0;

        // Visuals that already failed against the dead token stay broken until
        // the report re-renders, so recover explicitly when we got here from an
        // expiry error rather than from the scheduled check.
        if (recover) await report.reload();
      } catch (error) {
        if (cancelled) return;
        failures += 1;
        nextAttemptAt =
          Date.now() + RETRY_DELAY_MS * Math.min(failures, MAX_BACKOFF_STEPS);
      } finally {
        inFlight = false;
      }
    }

    refreshRef.current = refresh;

    function check() {
      const remaining = getTimeToExpiry(expiryRef.current);
      const isDue =
        remaining === null
          ? Date.now() - tokenObtainedAt >= FALLBACK_LIFETIME_MS
          : remaining <= REFRESH_MARGIN_MS;

      if (isDue) refresh();
    }

    // Check on the way back into the foreground too, so a returning tab does
    // not sit on an expired token for up to a full interval.
    function handleVisibilityChange() {
      if (document.visibilityState === 'visible') check();
    }

    check();
    const intervalId = setInterval(check, CHECK_INTERVAL_MS);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      cancelled = true;
      refreshRef.current = null;
      clearInterval(intervalId);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [serviceLine, tokenExpiry]);

  // Last line of defence: if a token still lapses, Power BI reports it through
  // the report's own error event. Recover from that instead of leaving the user
  // looking at "Access token has expired".
  const eventHandlers = useMemo(
    () =>
      new Map([
        [
          'error',
          (event) => {
            const detail = event?.detail ?? {};
            const text = `${detail.message ?? ''} ${detail.detailedMessage ?? ''}`;
            if (/token/i.test(text)) refreshRef.current?.(true);
          },
        ],
      ]),
    [],
  );

  return { setReport, eventHandlers };
};

export default useEmbedTokenRefresh;
